import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { labelSchema, type Label } from '@witty-board/shared'
import { z } from 'zod'
import { apiRequest } from './client.js'
import { buildQueryString } from '../lib/query-string.js'

export function labelsQueryKey(projectId: string) {
  return ['labels', projectId] as const
}

/**
 * Etiquetas del proyecto más las globales (así responde `GET /labels?projectId=`).
 * Solo lectura: no hay pantalla para crear etiquetas todavía (ver docs/DECISIONS.md, fase 4).
 */
export function labelsQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: labelsQueryKey(projectId),
    queryFn: () => apiRequest(`/labels${buildQueryString({ projectId })}`, z.array(labelSchema)),
    staleTime: 30_000,
  })
}

export function useLabelsQuery(projectId: string) {
  return useQuery(labelsQueryOptions(projectId))
}

/** Papelera (CLAUDE.md §6, fase 5): global o de un proyecto (incluye las globales, igual que la lista activa). */
export function deletedLabelsQueryKey(projectId: string | undefined) {
  return ['labels', 'trash', projectId] as const
}

export function useDeletedLabelsQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: deletedLabelsQueryKey(projectId),
    queryFn: () =>
      apiRequest(
        `/labels${buildQueryString({ projectId, onlyDeleted: true })}`,
        z.array(labelSchema),
      ),
  })
}

export function useRestoreLabelMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/labels/${id}/restore`, labelSchema, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['labels'], exact: false })
    },
  })
}

export type { Label }
